import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "../../data";

const HEADLINE = ["Train the Mechanics.", "Trust the Data.", "Own the Mound."];
const easing = [0.76, 0, 0.24, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.65, 0.95]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex h-[100svh] min-h-[640px] w-full items-end overflow-hidden"
    >
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Pitcher on the mound"
          className="h-full w-full object-cover object-center"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-10 bg-gradient-to-t from-black via-black/60 to-black/30"
      />
      <div className="absolute inset-0 z-10 grid-lines opacity-40" />

      {/* Data overlay chip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute right-6 top-28 z-20 hidden border border-[#00F0FF]/30 bg-black/40 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] backdrop-blur-sm md:block"
      >
        <div>REL. VELO // 94.2 MPH</div>
        <div className="mt-1 text-white/40">HIP-SHOULDER SEP // 48°</div>
      </motion.div>

      {/* Content */}
      <div className="relative z-20 mx-auto w-full max-w-7xl px-6 pb-16 md:px-12 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-[#00F0FF]" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#00F0FF]">
            Biomechanics-Driven Baseball Performance
          </span>
        </motion.div>

        <h1 className="font-heading text-white">
          {HEADLINE.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, ease: easing, delay: 0.5 + i * 0.15 }}
                className={`block text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-6xl md:text-8xl lg:text-[7.5vw] ${
                  i === 2 ? "text-[#00F0FF]" : ""
                }`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 max-w-xl font-body text-base text-white/70 md:text-lg"
        >
          A data-driven development system that measures how you move, builds a plan
          around your numbers, and turns velocity gains into real game results.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center"
        >
          <a
            href="#contact"
            data-testid="hero-cta-button"
            className="group inline-flex items-center gap-3 bg-[#00F0FF] px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.12em] text-black transition-transform duration-200 hover:scale-105"
          >
            Book a Performance Assessment
            <ArrowDown className="rotate-[-90deg] transition-transform duration-200 group-hover:translate-x-1" size={16} />
          </a>
          <a
            href="#programs"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center gap-2 border border-white/20 px-8 py-4 font-mono text-sm uppercase tracking-[0.12em] text-white transition-colors duration-200 hover:border-[#00F0FF] hover:text-[#00F0FF]"
          >
            View Programs
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-20 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.6 }}
          className="text-white/40"
        >
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
